import { useMemo, useCallback } from 'react';
import { create } from 'zustand';
import { useTickets, useSearchTickets } from '../../../../hooks/useApi';
import { useDebounce } from '../../../../hooks/useDebounce';
import { SupportTicket, TicketFilters } from '../../../../types';

interface TicketSearchState {
  searchTerm: string;
  statusFilter: string;
  tierFilter: string;
  cursor: string | undefined;
  direction: 'next' | 'prev';
  offset: number;
  page: number;
  limit: number;
}

interface TicketSearchActions {
  setSearchTerm: (term: string) => void;
  setStatusFilter: (status: string) => void;
  setTierFilter: (tier: string) => void;
  nextPage: (nextCursor?: string | null) => void;
  prevPage: (prevCursor?: string | null) => void;
  setPage: (page: number) => void;
  reset: () => void;
}

type TicketSearchStore = TicketSearchState & TicketSearchActions;

const initialState: TicketSearchState = {
  searchTerm: '',
  statusFilter: '',
  tierFilter: '',
  cursor: undefined,
  direction: 'next',
  offset: 0,
  page: 1,
  limit: 20,
};

const useTicketStore = create<TicketSearchStore>((set, get) => ({
  ...initialState,

  setSearchTerm: (term) => set({
    searchTerm: term,
    cursor: undefined,
    direction: 'next',
    offset: 0,
    page: 1,
  }),

  setStatusFilter: (status) => set({
    statusFilter: status,
    cursor: undefined,
    offset: 0,
    page: 1,
  }),

  setTierFilter: (tier) => set({
    tierFilter: tier,
    cursor: undefined,
    offset: 0,
    page: 1,
  }),

  nextPage: (nextCursor) => {
    const { offset, limit, page, searchTerm } = get();
    const isSearching = searchTerm.length >= 3;

    if (isSearching && nextCursor) {
      set({
        cursor: nextCursor,
        direction: 'next',
        offset: offset + limit,
      });
    } else if (!isSearching) {
      set({ page: page + 1 });
    }
  },

  prevPage: (prevCursor) => {
    const { offset, limit, page, searchTerm } = get();
    const isSearching = searchTerm.length >= 3;

    if (isSearching && prevCursor) {
      set({
        cursor: prevCursor,
        direction: 'prev',
        offset: Math.max(0, offset - limit),
      });
    } else if (!isSearching) {
      set({ page: Math.max(1, page - 1) });
    }
  },

  setPage: (page) => {
    const { searchTerm } = get();
    if (searchTerm.length >= 3) return;
    set({ page: Math.max(1, page), cursor: undefined, direction: 'next', offset: 0 });
  },

  reset: () => set(initialState),
}));

// Pagination info from API response
interface PaginationInfo {
  total: number;
  totalPages?: number;
  nextCursor?: string | null;
  prevCursor?: string | null;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

// Hook return type
interface UseTicketSearchReturn {
  // Data
  tickets: SupportTicket[];
  pagination: PaginationInfo | undefined;

  // Loading states
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;

  // Search state
  searchTerm: string;
  isSearchActive: boolean;
  debouncedSearch: string;

  // Filters
  statusFilter: string;
  tierFilter: string;

  // Pagination info for UI
  offset: number;
  page: number;

  // Actions
  setSearchTerm: (term: string) => void;
  setStatusFilter: (status: string) => void;
  setTierFilter: (tier: string) => void;
  nextPage: () => void;
  prevPage: () => void;
  setPage: (page: number) => void;
  reset: () => void;
}

export function useTicketSearch(): UseTicketSearchReturn {
  const {
    searchTerm,
    statusFilter,
    tierFilter,
    cursor,
    direction,
    offset,
    page,
    limit,
    setSearchTerm,
    setStatusFilter,
    setTierFilter,
    nextPage: storeNextPage,
    prevPage: storePrevPage,
    setPage: storeSetPage,
    reset,
  } = useTicketStore();

  // Debounce search term
  const debouncedSearch = useDebounce(searchTerm, 400);
  const isSearchActive = debouncedSearch.length >= 3;
  const searchParams = useMemo(() => {
    if (!isSearchActive) return {};

    const term = debouncedSearch.trim();
    if (!term.length) return {};
    const isEmail = term.includes('@');
    const isPhone = /^[\d+\-() ]+$/.test(term) && term.replace(/\D/g, '').length >= 6;

    return {
      ...(isEmail ? { email: term } : isPhone ? { phone: term } : { email: term, category: term }),
      cursor,
      direction,
      limit,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(tierFilter ? { tier: tierFilter } : {}),
    };
  }, [debouncedSearch, isSearchActive, cursor, direction, limit, statusFilter, tierFilter]);

  const listFilters: TicketFilters = useMemo(() => ({
    page,
    limit,
    ...(statusFilter ? { status: statusFilter as any } : {}),
    ...(tierFilter ? { tier: tierFilter } : {}),
  }), [page, limit, statusFilter, tierFilter]);

  // Query hooks
  const ticketsQuery = useTickets(listFilters);
  const searchQuery = useSearchTickets(searchParams);

  // Select active query based on search state
  const activeQuery = isSearchActive ? searchQuery : ticketsQuery;
  const tickets = activeQuery.data?.data?.tickets || [];
  const pagination = activeQuery.data?.data?.pagination;

  // Wrap pagination actions with cursor from response
  const handleNextPage = useCallback(() => {
    storeNextPage(pagination?.nextCursor);
  }, [storeNextPage, pagination?.nextCursor]);

  const handlePrevPage = useCallback(() => {
    storePrevPage(pagination?.prevCursor);
  }, [storePrevPage, pagination?.prevCursor]);

  return {
    tickets,
    pagination,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    error: activeQuery.error as Error | null,
    searchTerm,
    isSearchActive,
    debouncedSearch,
    statusFilter,
    tierFilter,
    offset,
    page,
    setSearchTerm,
    setStatusFilter,
    setTierFilter,
    nextPage: handleNextPage,
    prevPage: handlePrevPage,
    setPage: storeSetPage,
    reset,
  };
}

export { useTicketStore };
